import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    GetFormalOffersResponse,
    GetIwocapayPaymentLaterResponse
} from '@iwoca/lapi-client/edge';

import styles from './OfferChoiceForm.module.css';
import { Button, Skeleton } from '@iwoca/orion';
import { useGetFormalOffers } from '@api/lendingAPI/lapiHooks';
import { useDrawDownRepaymentScheduleIwocaPay } from './useDrawDownRepaymentScheduleIwocaPay';
import { Radio } from './RadioInput/RadioInput';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { offerChoiceValidator } from './OfferChoiceValidator';
import { SecurityChecksPrompt } from '../approvedView/SecurityChecksPrompt/SecurityChecksPrompt';
import { useGetSecurityCheckRequirements } from '../SLSecurityCheckBanner/SLSecurityCheckBanner';
import { getEcomOrderIdFromQueryParams } from '../../../../myAccount/drawDown/DrawDownHelper';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useTrackViewedOffers } from '../hooks/useTrackViewedOffers';
import { mapOfferDurationToMonths } from '../mapOfferDurationToMonths';
import { filterAvailableDurationsByPayLink } from './utils/filterAvailableDurationsByPaylink';
import { displayCurrency } from '../ANTCard/ANTCard';

type FormValues = {
    offerId: string;
};

export const OfferChoiceForm = ({
    payLink
}: {
    payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
    const { isAnyRequirementVisible } = useGetSecurityCheckRequirements();
    const stateKey = useStateKey();
    const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
    const orderId = getEcomOrderIdFromQueryParams();

    const initialFormValues: FormValues = {
        offerId: ''
    };
    const methods = useForm({
        defaultValues: initialFormValues,
        resolver: yupResolver(offerChoiceValidator),
        mode: 'all'
    });
    const handleSubmit = ({ offerId }: FormValues) => {
        // Copied from the saga. Will change as this feels bit confusing.
        const targetURL = new URL(
            `${window.location.protocol}//${window.location.host}/pay/checkout/`
        );

        targetURL.searchParams.append('payLinkId', payLink.id);
        targetURL.searchParams.append('offerId', offerId);
        if (orderId) {
            targetURL.searchParams.append('orderId', orderId);
        }

        if (stateKey) {
            targetURL.searchParams.append('state_key', stateKey);
        }

        tracking.ampli.selectedOffer({
            pay_link_id: payLink.id,
            offer_ids: formalOffers.map((offer) => offer.offer_id!),
            offer_id: offerId
        });

        window.location.href = targetURL.toString();
        return;
    };
    const onSubmit = methods.handleSubmit(handleSubmit);
    const { isValid } = methods.formState;

    useTrackViewedOffers({
        payLinkId: payLink.id,
        formalOffers: formalOffers,
        loadingApis: loadingFormalOffers
    });

    if (loadingFormalOffers)
        return (
            <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
            </>
        );

    if (!formalOffers) return null;

    return (
        <div>
            <FormProvider {...methods}>
                <div className={styles.subtitle}>Choose a payment option</div>
                <form
                    autoComplete="off"
                    className={styles.form}
                    onSubmit={onSubmit}
                >
                    <div className={styles.options}>
                        {filterAvailableDurationsByPayLink({
                            offers: formalOffers,
                            payLink
                        }).map((offer) => (
                            <OfferChoiceOption
                                key={offer.offer_id}
                                offer={offer}
                                payLink={payLink}
                            />
                        ))}
                    </div>
                    <SecurityChecksPrompt />
                    <Button aria-disabled={!isValid} disabled={!isValid}>
                        {isAnyRequirementVisible
                            ? 'Complete security checks'
                            : 'Finish checkout'}
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
};

const PAYMENT_OPTION_INPUT_ID = 'offerId';
const OfferChoiceOption = ({
    offer,
    payLink
}: {
    offer: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][number];
    payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
    const { register, watch } = useFormContext();
    const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);
    const isChecked = watchPaymentOption === offer.offer_id!;
    const id = offer.offer_id!;
    const duration = mapOfferDurationToMonths(offer);

    const {
        monthlyRepayment,
        interestRate,
        loadingDrawDownRepaymentScheduleIwocapay
    } = useDrawDownRepaymentScheduleIwocaPay({
        offer,
        payLink
    });

    if (loadingDrawDownRepaymentScheduleIwocapay) {
        return <Skeleton />;
    }

    if (monthlyRepayment === undefined || interestRate === undefined)
        return null;

    return (
        <Radio
            {...register(PAYMENT_OPTION_INPUT_ID)}
            id={id}
            checked={isChecked}
            value={id}
            label={
                <label className={styles.label} htmlFor={id}>
                    <span>
                        <b> Pay {displayCurrency(monthlyRepayment)} </b> / month
                        for {duration} months
                    </span>
                    <span className={styles.seperator}>|</span>
                    <span className={styles.interest}>
                        {interestRate === 0
                            ? 'interest-free'
                            : `${(interestRate * 100).toFixed(2)}% /month`}
                    </span>
                </label>
            }
            frame
        />
    );
};
